import { Component } from '@theme/component';
import { ThemeEvents, CartUpdateEvent } from '@theme/events';

/**
 * A custom element that displays a cart icon with an inline item count.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} cartBubble - The cart bubble element (visually hidden, kept for a11y).
 * @property {HTMLElement} cartBubbleText - The cart bubble text element.
 * @property {HTMLElement} cartBubbleCount - The cart bubble count element.
 * @property {HTMLElement | HTMLElement[]} [cartCount] - Inline cart count label(s).
 *
 * @extends {Component<Refs>}
 */
class CartIcon extends Component {
  requiredRefs = ['cartBubble', 'cartBubbleText', 'cartBubbleCount'];

  /** @type {number} */
  get currentCartCount() {
    return parseInt(this.refs.cartBubbleCount.textContent ?? '0', 10) || 0;
  }

  set currentCartCount(value) {
    const displayValue = value < 100 ? String(value) : '';
    this.refs.cartBubbleCount.textContent = displayValue;
    this.#updateInlineCounts(value);
  }

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
    window.addEventListener('pageshow', this.onPageShow);
    this.ensureCartBubbleIsCorrect();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
    window.removeEventListener('pageshow', this.onPageShow);
  }

  /**
   * Handles the page show event when the page is restored from cache.
   * @param {PageTransitionEvent} event - The page show event.
   */
  onPageShow = (event) => {
    if (event.persisted) {
      this.ensureCartBubbleIsCorrect();
    }
  };

  /**
   * Handles the cart update event.
   * @param {CartUpdateEvent} event - The cart update event.
   */
  onCartUpdate = async (event) => {
    const itemCount = event.detail.data?.itemCount ?? 0;
    const comingFromProductForm = event.detail.data?.source === 'product-form-component';

    this.renderCartBubble(itemCount, comingFromProductForm);
  };

  /**
   * Renders the cart count (inline label + hidden bubble for a11y).
   * @param {number} itemCount - The number of items in the cart.
   * @param {boolean} comingFromProductForm - Whether the cart update is coming from the product form.
   * @param {boolean} [animate=true] - Unused; kept for call-site compatibility.
   */
  renderCartBubble = async (itemCount, comingFromProductForm, animate = true) => {
    void animate;

    const nextCount = comingFromProductForm ? this.currentCartCount + itemCount : itemCount;

    this.refs.cartBubbleCount.classList.toggle('hidden', nextCount === 0);
    this.refs.cartBubble.classList.toggle('visually-hidden', nextCount === 0);

    this.currentCartCount = nextCount;

    this.classList.toggle('header-actions__cart-icon--has-cart', nextCount > 0);

    sessionStorage.setItem(
      'cart-count',
      JSON.stringify({
        value: String(this.currentCartCount),
        timestamp: Date.now(),
      })
    );
  };

  /**
   * Updates visible inline count labels to " (n)" or hides them when empty.
   * @param {number} value
   */
  #updateInlineCounts(value) {
    const countRefs = this.refs.cartCount;
    if (!countRefs) return;

    const elements = Array.isArray(countRefs) ? countRefs : [countRefs];
    const label = value > 0 ? `\u00A0(${value < 100 ? value : '99+'})` : '';

    for (const el of elements) {
      el.textContent = label;
      el.classList.toggle('hidden', value === 0);
    }
  }

  /**
   * Checks if the cart count is correct against recent session storage.
   */
  ensureCartBubbleIsCorrect = () => {
    if (!this.refs.cartBubbleCount) return;

    const sessionStorageCount = sessionStorage.getItem('cart-count');

    if (sessionStorageCount === null) {
      this.#updateInlineCounts(this.currentCartCount);
      return;
    }

    const visibleCount = this.refs.cartBubbleCount.textContent;

    try {
      const { value, timestamp } = JSON.parse(sessionStorageCount);

      if (value === visibleCount) {
        this.#updateInlineCounts(parseInt(value, 10) || 0);
        return;
      }

      if (Date.now() - timestamp < 10000) {
        const count = parseInt(value, 10);

        if (count >= 0) {
          this.renderCartBubble(count, false, false);
        }
      } else {
        this.#updateInlineCounts(this.currentCartCount);
      }
    } catch (_) {
      this.#updateInlineCounts(this.currentCartCount);
    }
  };
}

if (!customElements.get('cart-icon')) {
  customElements.define('cart-icon', CartIcon);
}
